/**
 * Update Prompt Component
 *
 * Shows a toast/banner when a new version of the app is available.
 * Allows users to update immediately or dismiss.
 */

import { useState } from 'react';
import { useServiceWorker } from '../../hooks/useServiceWorker';
import { useAuth } from '../../contexts/AuthContext';

export function UpdatePrompt() {
  const { needRefresh, updateServiceWorker } = useServiceWorker();
  const { isAuthenticated } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (!needRefresh || dismissed) {
    return null;
  }

  // Dock to the bottom edge rather than floating a card partway up the screen:
  // the old `bottom-20` inset was sized for the bottom nav, so on the public
  // tournament hub (no nav — logged-out spectators don't get one) the toast
  // landed in the middle of the podium with content still visible below it.
  // Signed-in users keep the nav clear; everyone else gets the home-indicator
  // strip reserved instead.
  const bottomOffset = isAuthenticated
    ? 'calc(4rem + max(env(safe-area-inset-bottom), 0.5rem))'
    : '0px';

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed inset-x-0 z-50 bg-bbx-primary-600 text-white shadow-lg border-t border-white/20 px-4 py-3 flex items-center justify-between gap-3"
      style={{
        bottom: bottomOffset,
        paddingBottom: isAuthenticated
          ? undefined
          : 'max(env(safe-area-inset-bottom), 0.75rem)',
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <svg
          className="w-6 h-6 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <span className="text-sm font-medium truncate">
          New version available!
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={updateServiceWorker}
          className="px-4 py-2 bg-white text-bbx-primary-600 font-medium rounded-lg hover:bg-bbx-dark-100 transition-colors min-h-[44px] min-w-[44px]"
        >
          Update
        </button>
        {/* Nothing else can clear this bar, so without a dismiss it sits over
            the foot of the page for the rest of the session. */}
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss update notice"
          className="p-2 rounded-lg hover:bg-white/10 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
